import { useState } from "react";
import { api, notifyError } from "@/lib/api";
import { Calculator, Warning, CheckCircle, Info } from "@phosphor-icons/react";
import { Field } from "./shared";

const usd = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const TILES = [
  { key: "asc_base", label: "Base amount" },
  { key: "excess_qre", label: "Excess QRE" },
  { key: "gross_credit", label: "Gross credit" },
  { key: "net_credit", label: "Net credit", color: "#00A86B" },
  { key: "applied_to_ct", label: "Applied to CT" },
  { key: "payroll_offset_available", label: "Payroll offset", color: "#FFB020" },
];

export default function UsRDCalc({ onSaved }) {
  const [f, setF] = useState({ method: "asc", current_qre: "", p1: "", p2: "", p3: "", fixed_base_percentage: "", avg_gross_receipts: "", corporation_tax_liability: "", tax_year: new Date().getFullYear(), reduce_280c: false, qualified_small_business: false });
  const [calc, setCalc] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const priors = [f.p1, f.p2, f.p3].map((x) => Number(x || 0)).filter((x) => x > 0);
      const r = await api.post("/tax/calculate/us-rd", {
        method: f.method,
        current_qre: Number(f.current_qre),
        prior_qres: priors,
        fixed_base_percentage: Number(f.fixed_base_percentage || 0),
        avg_gross_receipts: Number(f.avg_gross_receipts || 0),
        corporation_tax_liability: Number(f.corporation_tax_liability || 0),
        reduce_280c: f.reduce_280c,
        qualified_small_business: f.qualified_small_business,
        tax_year: Number(f.tax_year),
      });
      setCalc(r.data);
      onSaved && onSaved();
    } catch (err) { notifyError(err); }
    finally { setBusy(false); }
  };

  const r = calc?.results;
  const isRegular = f.method === "regular";

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={run} className="glass rounded-2xl p-6 space-y-4">
        <div>
          <div className="flex items-center gap-2 eyebrow mb-1">
            <span className="px-2 py-0.5 rounded-full text-[9px] font-bold" style={{ background: "#C77DFF22", color: "#C77DFF", border: "1px solid #C77DFF" }}>US</span>
            Federal R&D credit
          </div>
          <p className="text-white/55 text-sm">Estimate the IRC §41 credit using either the Alternative Simplified Credit or the traditional Regular method.</p>
        </div>
        <div className="flex gap-2">
          {[["asc", "ASC (simplified)"], ["regular", "Regular"]].map(([id, label]) => (
            <button key={id} type="button" onClick={() => set("method", id)} data-testid={`usrd-method-${id}`}
              className={`px-4 py-2 rounded-lg text-sm ${f.method === id ? "bg-[#C77DFF]/15 text-[#C77DFF] border border-[#C77DFF]/50" : "bg-black/20 text-white/60 border border-white/8"}`}>{label}</button>
          ))}
        </div>
        <Field label="Current-year QREs ($)" type="number" step="0.01" required data-testid="usrd-current" value={f.current_qre} onChange={(e) => set("current_qre", e.target.value)} />
        {isRegular ? (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Fixed-base % (of gross receipts)" type="number" step="0.01" data-testid="usrd-fbp" value={f.fixed_base_percentage} onChange={(e) => set("fixed_base_percentage", e.target.value)} />
            <Field label="Avg gross receipts (prior 4 yr, $)" type="number" step="0.01" data-testid="usrd-agr" value={f.avg_gross_receipts} onChange={(e) => set("avg_gross_receipts", e.target.value)} />
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            <Field label="Prior yr 1 ($)" type="number" step="0.01" data-testid="usrd-p1" value={f.p1} onChange={(e) => set("p1", e.target.value)} />
            <Field label="Prior yr 2 ($)" type="number" step="0.01" data-testid="usrd-p2" value={f.p2} onChange={(e) => set("p2", e.target.value)} />
            <Field label="Prior yr 3 ($)" type="number" step="0.01" data-testid="usrd-p3" value={f.p3} onChange={(e) => set("p3", e.target.value)} />
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Corporate tax liability ($)" type="number" step="0.01" data-testid="usrd-ct" value={f.corporation_tax_liability} onChange={(e) => set("corporation_tax_liability", e.target.value)} />
          <Field label="Tax year" type="number" data-testid="usrd-year" value={f.tax_year} onChange={(e) => set("tax_year", e.target.value)} />
        </div>
        <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" className="w-auto" data-testid="usrd-280c" checked={f.reduce_280c} onChange={(e) => set("reduce_280c", e.target.checked)} /> Take reduced-credit (§280C) election</label>
        <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" className="w-auto" data-testid="usrd-qsb" checked={f.qualified_small_business} onChange={(e) => set("qualified_small_business", e.target.checked)} /> Qualified Small Business (payroll-tax offset)</label>
        <button type="submit" disabled={busy} className="btn-gold w-full justify-center" data-testid="usrd-calculate"><Calculator size={16} weight="fill" /> {busy ? "Calculating…" : "Calculate R&D credit"}</button>
      </form>

      {r ? (
        <div className="glass rounded-2xl p-6 space-y-5" data-testid="usrd-result">
          <div className="eyebrow">Estimated credit · {r.method}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {TILES.map((t) => (
              <div key={t.key} className="rounded-xl bg-black/30 border border-white/5 p-4" data-testid={`usrd-tile-${t.key}`}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{t.label}</div>
                <div className="mono text-2xl mt-2" style={t.color ? { color: t.color } : undefined}>{usd(r[t.key])}</div>
              </div>
            ))}
          </div>
          {r.warnings?.length > 0 && (
            <div className="space-y-2">
              {r.warnings.map((w) => (
                <div key={w} className="flex items-start gap-2 text-sm rounded-lg px-3 py-2" style={{ background: "#FFB02014", border: "1px solid #FFB02044" }} data-testid="usrd-warning">
                  <Warning size={16} weight="fill" className="text-[#FFB020] mt-0.5 shrink-0" /><span className="text-white/80">{w}</span>
                </div>
              ))}
            </div>
          )}
          <div>
            <div className="eyebrow mb-2">Next steps</div>
            <ul className="space-y-1.5">
              {calc.next_steps.map((s) => (
                <li key={s} className="flex items-start gap-2 text-sm text-white/75"><CheckCircle size={16} weight="fill" className="text-[#00A86B] mt-0.5 shrink-0" /> {s}</li>
              ))}
            </ul>
          </div>
          <div className="flex items-start gap-2 text-xs text-white/50 border-t border-white/5 pt-4"><Info size={14} className="mt-0.5 shrink-0" /> {calc.disclaimer}</div>
        </div>
      ) : (
        <div className="glass rounded-2xl p-6 flex items-center justify-center text-white/40 text-sm text-center">Enter your US QREs to estimate the federal R&D credit (ASC method).</div>
      )}
    </div>
  );
}
